import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { PbosDashboardLayout } from "@/components/layout/PbosDashboardLayout";
import { apiFetch } from "@/lib/api";
import { authClient, useSession } from "@/lib/auth-client";
import { denyRole, requireAdmin, requireAuth, requireRole } from "@/lib/route-guards";
import { RootLayout } from "@/routes/__root";
import { ChangePasswordPage } from "@/routes/change-password";
import { AuditTrailPage } from "@/routes/dashboard/admin/audit";
import { BillingPeriodsPage } from "@/routes/dashboard/admin/billing-periods";
import { AdminConfigPage, AdminConfigSectionPage } from "@/routes/dashboard/admin/config";
import { CostCodesPage } from "@/routes/dashboard/admin/cost-codes";
import { FolderTemplatesPage } from "@/routes/dashboard/admin/folder-templates";
import { GcLineItemTemplatesPage } from "@/routes/dashboard/admin/gc-line-item-templates";
import { AdminPage } from "@/routes/dashboard/admin/index";
import { IntegrationsPage } from "@/routes/dashboard/admin/integrations";
import { MilestoneTemplatesPage } from "@/routes/dashboard/admin/milestone-templates";
import { ProjectTeamRolesPage } from "@/routes/dashboard/admin/project-team-roles";
import { TaskTemplatesPage } from "@/routes/dashboard/admin/task-templates";
import { AdminUsersPage } from "@/routes/dashboard/admin/users";
import { CommitmentDetailPage } from "@/routes/dashboard/commitment-detail";
import { CommitmentEditPage } from "@/routes/dashboard/commitment-edit";
import { CommitmentNewPage } from "@/routes/dashboard/commitment-new";
import { CompaniesPage } from "@/routes/dashboard/companies";
import { CompanyDetailPage } from "@/routes/dashboard/company-detail";
import { CompliancePage } from "@/routes/dashboard/compliance";
import { ContactsPage } from "@/routes/dashboard/contacts";
import { ContractorDashboardPage } from "@/routes/dashboard/contractor-dashboard";
import { DailyLogDetailPage } from "@/routes/dashboard/daily-log-detail";
import { DailyLogsPage } from "@/routes/dashboard/daily-logs";
import { DocumentSharesPage } from "@/routes/dashboard/document-shares";
import { DocumentsPage } from "@/routes/dashboard/documents";
import { GcCostTrackerPage } from "@/routes/dashboard/gc-cost-tracker";
import { GeneralRequirementsLayout } from "@/routes/dashboard/general-requirements";
import { GlossaryPage } from "@/routes/dashboard/glossary";
import { GrInboxPage } from "@/routes/dashboard/gr-inbox";
import { GrIntakePage } from "@/routes/dashboard/gr-intake";
import { GrPendingVistaPage } from "@/routes/dashboard/gr-pending-vista";
import { DashboardPage } from "@/routes/dashboard/index";
import { InspectionDetailPage } from "@/routes/dashboard/inspection-detail";
import { InspectionNewPage } from "@/routes/dashboard/inspection-new";
import { InspectionsPage } from "@/routes/dashboard/inspections";
import { JobCostPage } from "@/routes/dashboard/job-cost";
import { MyCommitmentsPage } from "@/routes/dashboard/my-commitments";
import { MyCompanyPage } from "@/routes/dashboard/my-company";
import { MyPayAppsPage } from "@/routes/dashboard/my-pay-apps";
import { NotificationsPage } from "@/routes/dashboard/notifications";
import { OwnerPayAppsPage } from "@/routes/dashboard/pay-apps-owner";
import { OwnerPayAppDetailPage } from "@/routes/dashboard/pay-apps-owner-detail";
import { SubcontractPayAppsPage } from "@/routes/dashboard/pay-apps-subcontract";
import { SubcontractPayAppDetailPage } from "@/routes/dashboard/pay-apps-subcontract-detail";
import { ProfilePage } from "@/routes/dashboard/profile";
import { ProjectDetailPage } from "@/routes/dashboard/project-detail";
import { ProjectsPage } from "@/routes/dashboard/projects";
import { PunchListPage } from "@/routes/dashboard/punch-list";
import { PunchListDetailPage } from "@/routes/dashboard/punch-list-detail";
import { ReleaseNotesPage } from "@/routes/dashboard/release-notes";
import { RfisPage } from "@/routes/dashboard/rfis";
import { SafetyIncidentDetailPage } from "@/routes/dashboard/safety-incident-detail";
import { SafetyIncidentsPage } from "@/routes/dashboard/safety-incidents";
import { SubmittalsPage } from "@/routes/dashboard/submittals";
import { TimelinePage } from "@/routes/dashboard/timeline";
import { ForgotPasswordPage } from "@/routes/forgot-password";
import { LoginPage } from "@/routes/login";
import { OnboardingPage } from "@/routes/onboarding";
import { ResetPasswordPage } from "@/routes/reset-password";
import { PublicShareViewer } from "@/routes/share-file";
import { PublicFolderShareViewer } from "@/routes/share-folder";

// --- Root ---
const rootRoute = createRootRoute({ component: RootLayout });

// --- Public routes ---
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/forgot-password",
  component: ForgotPasswordPage,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset-password",
  component: ResetPasswordPage,
});

const shareFileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/share/file/$token",
  component: function ShareFilePage() {
    const { token } = shareFileRoute.useParams();
    return <PublicShareViewer token={token} />;
  },
});

const shareFolderRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/share/folder/$token",
  component: function ShareFolderPage() {
    const { token } = shareFolderRoute.useParams();
    return <PublicFolderShareViewer token={token} />;
  },
});

const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/onboarding",
  beforeLoad: async () => {
    const session = await authClient.getSession();
    if (!session.data?.user) throw redirect({ to: "/login" });
  },
  component: OnboardingPage,
});

const changePasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/change-password",
  beforeLoad: async () => {
    const session = await authClient.getSession();
    if (!session.data?.user) throw redirect({ to: "/login" });
  },
  component: ChangePasswordPage,
});

// --- Dashboard layout (auth required) ---
const dashboardLayout = createRoute({
  getParentRoute: () => rootRoute,
  id: "dashboard",
  beforeLoad: async () => {
    await requireAuth();
    const session = await authClient.getSession();
    if (session.data?.user) {
      try {
        const state = await apiFetch<{ onboardingStatus: string; mustChangePassword: boolean }>(
          "/onboarding",
        );
        if (state.mustChangePassword) {
          throw redirect({ to: "/change-password" });
        }
        if (state.onboardingStatus !== "complete") {
          throw redirect({ to: "/onboarding" });
        }
      } catch (err) {
        if (err instanceof Response || (err && typeof err === "object" && "to" in err)) throw err;
      }
    }
  },
  component: PbosDashboardLayout,
});

// --- Dashboard index ---
function DashboardIndex() {
  const { data: session } = useSession();
  const role = session?.user?.role;
  return role === "contractor" ? <ContractorDashboardPage /> : <DashboardPage />;
}

const dashboardIndex = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/",
  component: DashboardIndex,
});

// --- Portfolio ---
const timelineRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/timeline",
  beforeLoad: denyRole(["contractor"]),
  component: TimelinePage,
});

const projectsRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/projects",
  component: ProjectsPage,
});

const projectDetailRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/projects/$projectId",
  validateSearch: (search: Record<string, unknown>) => ({
    tab: typeof search.tab === "string" ? search.tab : "overview",
  }),
  component: ProjectDetailPage,
});

const gcCostTrackerRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/projects/$projectId/gc-cost-tracker",
  beforeLoad: denyRole(["contractor"]),
  component: GcCostTrackerPage,
});

// --- Coordination ---
const rfisRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/rfis",
  component: RfisPage,
});

const submittalsRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/submittals",
  component: SubmittalsPage,
});

const dailyLogsRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/daily-logs",
  beforeLoad: denyRole(["contractor"]),
  component: DailyLogsPage,
});

const dailyLogDetailRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/daily-logs/$id",
  beforeLoad: denyRole(["contractor"]),
  component: DailyLogDetailPage,
});

const punchListRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/punch-list",
  component: PunchListPage,
});

const punchListDetailRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/punch-list/$id",
  component: PunchListDetailPage,
});

// --- Finance ---
const myCommitmentsRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/my-commitments",
  component: MyCommitmentsPage,
});

const myCommitmentDetailRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/my-commitments/$id",
  component: CommitmentDetailPage,
});

const commitmentDetailRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/commitments/$id",
  component: CommitmentDetailPage,
});

const commitmentEditRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/commitments/$id/edit",
  component: CommitmentEditPage,
});

const commitmentNewRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/commitments/new",
  validateSearch: (search: Record<string, unknown>) => ({
    projectId: typeof search.projectId === "string" ? search.projectId : "",
    budgetLineId: typeof search.budgetLineId === "string" ? search.budgetLineId : "",
  }),
  component: CommitmentNewPage,
});

const myPayAppsRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/my-pay-apps",
  component: MyPayAppsPage,
});

const payApplicationsRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/pay-applications",
  beforeLoad: ({ location }) => {
    if (location.pathname === "/pay-applications") {
      throw redirect({ to: "/pay-applications/subcontract" });
    }
  },
});

const payApplicationsSubcontractRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/pay-applications/subcontract",
  component: SubcontractPayAppsPage,
});

const payApplicationsSubcontractDetailRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/pay-applications/subcontract/$id",
  component: SubcontractPayAppDetailPage,
});

const payApplicationsOwnerRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/pay-applications/owner",
  component: OwnerPayAppsPage,
});

const payApplicationsOwnerDetailRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/pay-applications/owner/$id",
  component: OwnerPayAppDetailPage,
});

const complianceRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/compliance",
  component: CompliancePage,
});

// --- General Requirements ---
const generalRequirementsRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/general-requirements",
  beforeLoad: denyRole(["contractor"]),
  component: GeneralRequirementsLayout,
});

const grIndexRoute = createRoute({
  getParentRoute: () => generalRequirementsRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/general-requirements/inbox" });
  },
});

const grInboxRoute = createRoute({
  getParentRoute: () => generalRequirementsRoute,
  path: "/inbox",
  component: GrInboxPage,
});

const grIntakeRoute = createRoute({
  getParentRoute: () => generalRequirementsRoute,
  path: "/intake",
  component: GrIntakePage,
});

const grPendingVistaRoute = createRoute({
  getParentRoute: () => generalRequirementsRoute,
  path: "/pending-vista",
  component: GrPendingVistaPage,
});

// --- Contacts & Companies ---
const contactsRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/contacts",
  beforeLoad: denyRole(["contractor"]),
  component: ContactsPage,
});

const documentsRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/documents",
  beforeLoad: denyRole(["contractor"]),
  validateSearch: (search: Record<string, unknown>) => ({
    tab: search.tab === "folders" ? "folders" : ("files" as "files" | "folders"),
    search: typeof search.search === "string" ? search.search : "",
    projectIds: typeof search.projectIds === "string" ? search.projectIds : "",
    folderPaths: typeof search.folderPaths === "string" ? search.folderPaths : "",
  }),
  component: DocumentsPage,
});

const documentSharesRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/document-shares",
  beforeLoad: denyRole(["contractor"]),
  component: DocumentSharesPage,
});

const inspectionsRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/inspections",
  component: InspectionsPage,
});

const inspectionDetailRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/inspections/$id",
  component: InspectionDetailPage,
});

const inspectionNewRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/inspections/new",
  component: InspectionNewPage,
});

const safetyIncidentsRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/safety-incidents",
  beforeLoad: denyRole(["contractor"]),
  component: SafetyIncidentsPage,
});

const safetyIncidentDetailRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/safety-incidents/$id",
  beforeLoad: denyRole(["contractor"]),
  component: SafetyIncidentDetailPage,
});

const jobCostRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/job-cost",
  beforeLoad: denyRole(["contractor"]),
  component: JobCostPage,
});

const companiesRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/companies",
  beforeLoad: denyRole(["contractor"]),
  component: CompaniesPage,
});

const companyDetailRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/companies/$id",
  beforeLoad: denyRole(["contractor"]),
  component: CompanyDetailPage,
});

const myCompanyRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/my-company",
  beforeLoad: requireRole(["contractor"]),
  component: MyCompanyPage,
});

const glossaryRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/glossary",
  beforeLoad: denyRole(["contractor"]),
  component: GlossaryPage,
});

const releaseNotesRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/release-notes",
  component: ReleaseNotesPage,
});

const profileRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/profile",
  component: ProfilePage,
});

const notificationsRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/notifications",
  component: NotificationsPage,
});

// --- Admin layout ---
function AdminLayout() {
  return <Outlet />;
}

const adminLayout = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/admin",
  beforeLoad: requireAdmin,
  component: AdminLayout,
});

const adminIndexRoute = createRoute({
  getParentRoute: () => adminLayout,
  path: "/",
  component: AdminPage,
});

const adminUsersRoute = createRoute({
  getParentRoute: () => adminLayout,
  path: "/users",
  component: AdminUsersPage,
});

const adminConfigRoute = createRoute({
  getParentRoute: () => adminLayout,
  path: "/config",
  component: AdminConfigPage,
});

const adminConfigSectionRoute = createRoute({
  getParentRoute: () => adminLayout,
  path: "/config/$section",
  component: AdminConfigSectionPage,
});

const adminCostCodesRoute = createRoute({
  getParentRoute: () => adminLayout,
  path: "/config/cost-codes",
  component: CostCodesPage,
});

const adminTaskTemplatesRoute = createRoute({
  getParentRoute: () => adminLayout,
  path: "/config/task-templates",
  component: TaskTemplatesPage,
});

const adminMilestoneTemplatesRoute = createRoute({
  getParentRoute: () => adminLayout,
  path: "/config/milestone-templates",
  component: MilestoneTemplatesPage,
});

const adminFolderTemplatesRoute = createRoute({
  getParentRoute: () => adminLayout,
  path: "/config/folder-templates",
  component: FolderTemplatesPage,
});

const adminGcTemplatesRoute = createRoute({
  getParentRoute: () => adminLayout,
  path: "/config/gc-line-item-templates",
  component: GcLineItemTemplatesPage,
});

const adminBillingPeriodsRoute = createRoute({
  getParentRoute: () => adminLayout,
  path: "/config/billing-periods",
  component: BillingPeriodsPage,
});

const adminProjectTeamRolesRoute = createRoute({
  getParentRoute: () => adminLayout,
  path: "/config/project-team-roles",
  component: ProjectTeamRolesPage,
});

const adminAuditRoute = createRoute({
  getParentRoute: () => adminLayout,
  path: "/audit",
  component: AuditTrailPage,
});

const adminIntegrationsRoute = createRoute({
  getParentRoute: () => adminLayout,
  path: "/integrations",
  component: IntegrationsPage,
});

// --- Route tree ---
const routeTree = rootRoute.addChildren([
  loginRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  shareFileRoute,
  shareFolderRoute,
  onboardingRoute,
  changePasswordRoute,
  dashboardLayout.addChildren([
    dashboardIndex,
    timelineRoute,
    projectsRoute,
    projectDetailRoute,
    gcCostTrackerRoute,
    rfisRoute,
    submittalsRoute,
    dailyLogsRoute,
    dailyLogDetailRoute,
    punchListRoute,
    punchListDetailRoute,
    myCommitmentsRoute,
    myCommitmentDetailRoute,
    commitmentNewRoute,
    commitmentDetailRoute,
    commitmentEditRoute,
    myPayAppsRoute,
    payApplicationsRoute,
    payApplicationsSubcontractRoute,
    payApplicationsSubcontractDetailRoute,
    payApplicationsOwnerRoute,
    payApplicationsOwnerDetailRoute,
    complianceRoute,
    generalRequirementsRoute.addChildren([
      grIndexRoute,
      grInboxRoute,
      grIntakeRoute,
      grPendingVistaRoute,
    ]),
    contactsRoute,
    documentsRoute,
    documentSharesRoute,
    inspectionsRoute,
    inspectionDetailRoute,
    inspectionNewRoute,
    safetyIncidentsRoute,
    safetyIncidentDetailRoute,
    jobCostRoute,
    companiesRoute,
    companyDetailRoute,
    myCompanyRoute,
    glossaryRoute,
    releaseNotesRoute,
    profileRoute,
    notificationsRoute,
    adminLayout.addChildren([
      adminIndexRoute,
      adminUsersRoute,
      adminConfigRoute,
      adminCostCodesRoute,
      adminTaskTemplatesRoute,
      adminMilestoneTemplatesRoute,
      adminFolderTemplatesRoute,
      adminGcTemplatesRoute,
      adminBillingPeriodsRoute,
      adminProjectTeamRolesRoute,
      adminConfigSectionRoute,
      adminAuditRoute,
      adminIntegrationsRoute,
    ]),
  ]),
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
